import Image from "./Image";
import { Check, ChevronUp, CreditCard, MapPin } from "lucide-react";
/* Portrait media panels for the platform-capability cards. Tolls keeps an opaque panel because it
   needs a road surface to draw on; telematics and payments sit on a transparent frame so their
   content floats directly on the card with no backing. */
const PANEL = "relative mx-auto aspect-[3/4] w-full max-w-[236px] overflow-hidden rounded-xl border border-white/10 bg-[#20242c] shadow-[0_18px_40px_rgba(0,0,0,0.5)]";
/* TELEMATICS lives in its own client component (scroll-triggered flip reveal). */
export { TelematicsLogoCollage } from "./TelematicsLogoCollage";
/* ── PAYMENTS — a cross-fading carousel of rental payment terminals followed by a car-rental
   checkout order summary. The device photos rotate in, then the checkout card appears as the
   final slide; its "Book Trip" button pulses to draw the eye to the completed-payment moment. */
const PAYMENT_DEVICES = [
    { src: "/images/payments/terminal-black-screen.webp", alt: "Countertop payment terminal" },
    { src: "/images/payments/terminal-white-handheld.webp", alt: "Handheld rental payment terminal" },
    { src: "/images/payments/terminal-white-tap.webp", alt: "Contactless tap-to-pay terminal" },
];
export function PaymentsCarousel() {
    return (<div className="relative mx-auto flex aspect-[3/4] w-full max-w-[236px] items-center justify-center" role="img" aria-label="Rental payment terminals cycling into a car rental checkout order summary totaling $189.00 for a 3-day Tesla Model 3 booking, with a Book Trip button">
      {PAYMENT_DEVICES.map((device, i) => (<div key={device.src} className="fc-carousel-slide" style={{ animationDelay: `${i === 0 ? 0 : (i + 1) * 4}s` }}>
          <Image src={device.src || "/placeholder.svg"} alt={device.alt} width={1024} height={768} className="h-auto w-full max-w-full scale-[1.45] object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.55)]"/>
        </div>))}

      {/* Second slide — the checkout order summary, right after the first terminal. */}
      <div className="fc-carousel-slide" style={{ animationDelay: "4s" }}>
      <div className="relative w-full overflow-hidden rounded-2xl bg-white text-[#0b0b0f] shadow-[0_18px_40px_rgba(0,0,0,0.5)]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-black/[0.08] px-3.5 py-3">
          <span className="flex items-center gap-1 font-sans text-[11px] font-semibold text-[#0b0b0f]">
            Order summary
            <ChevronUp aria-hidden="true" className="h-3 w-3 text-[#0b0b0f]/50"/>
          </span>
          <span className="font-sans text-[13px] font-bold text-[#0b0b0f]">$189.00</span>
        </div>

        <div className="flex flex-col gap-3 px-3.5 py-3">
          {/* Line item */}
          <div className="flex items-center gap-2.5">
            <span className="relative shrink-0">
              <Image src="/images/payments/rental-car-thumb.webp" alt="Tesla Model 3 rental" width={96} height={96} className="h-11 w-11 rounded-lg object-cover"/>
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-sans text-[11px] font-semibold leading-tight text-[#0b0b0f]">Tesla Model 3</p>
              <p className="font-sans text-[9px] text-[#0b0b0f]/50">3 days · Standard</p>
            </div>
            <span className="font-sans text-[11px] font-semibold text-[#0b0b0f]">$180.00</span>
          </div>

          {/* Discount code */}
          <div className="flex items-center gap-2">
            <span className="flex-1 rounded-lg border border-black/[0.12] px-2.5 py-2 font-sans text-[10px] text-[#0b0b0f]/40">
              Discount code
            </span>
            <span className="rounded-lg bg-black/[0.05] px-3 py-2 font-sans text-[10px] font-semibold text-[#0b0b0f]/70">
              Apply
            </span>
          </div>

          {/* Totals */}
          <div className="flex flex-col gap-1 border-t border-black/[0.08] pt-2.5">
            <div className="flex items-center justify-between font-sans text-[10px] text-[#0b0b0f]/70">
              <span>Subtotal</span>
              <span>$180.00</span>
            </div>
            <div className="flex items-center justify-between font-sans text-[10px] text-[#0b0b0f]/70">
              <span>Delivery</span>
              <span className="font-semibold text-[#0b0b0f]">FREE</span>
            </div>
            <div className="flex items-center justify-between font-sans text-[10px] text-[#0b0b0f]/70">
              <span>Estimated taxes</span>
              <span>$9.00</span>
            </div>
            <div className="mt-1 flex items-center justify-between font-sans text-[13px] font-bold text-[#0b0b0f]">
              <span>Total</span>
              <span>$189.00</span>
            </div>
          </div>

          {/* Book Trip button — pulses to draw the eye */}
          <button type="button" tabIndex={-1} className="pm-pulse mt-1 w-full rounded-xl bg-[#6d3bf5] py-2.5 text-center font-sans text-[12px] font-bold text-white">
            Book Trip
          </button>
          </div>
        </div>
      </div>
    </div>);
}
/* ── TOLLS — a rebuilt white "Toll summary" card, matched to the payments checkout card, showing
   four Florida toll events auto-captured and billed back to the renter's card on file. */
const TOLL_EVENTS = [
    { badge: "FL", name: "Florida Turnpike", when: "Mar 2, 2025, 8:14 AM", amount: "$8.50" },
    { badge: "836", name: "SR 836 Dolphin Expressway", when: "Mar 2, 2025, 2:37 PM", amount: "$6.75" },
    { badge: "528", name: "SR 528 Beachline Expressway", when: "Mar 3, 2025, 11:21 AM", amount: "$6.25" },
    { badge: "95", name: "I-95 Express Lanes", when: "Mar 4, 2025, 6:48 PM", amount: "$6.35" },
];
export function TollsMapVertical() {
    return (<div className="relative mx-auto flex aspect-[3/4] w-full max-w-[236px] items-center justify-center" role="img" aria-label="An automated toll summary listing four Florida toll events totaling $27.85, paid automatically to the renter's card on file">
      <div className="relative w-full overflow-hidden rounded-2xl bg-white text-[#0b0b0f] shadow-[0_18px_40px_rgba(0,0,0,0.5)]">
        {/* Beam: green light sweeps left→right and fades into the bottom-right stroke, synced to the Paid banner */}
        <span aria-hidden="true" className="anim-invoice-beam pointer-events-none absolute inset-0 z-[5] rounded-2xl"/>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-black/[0.08] px-3 py-2">
          <span className="flex items-center gap-1 font-sans text-[10px] font-semibold text-[#0b0b0f]">
            Toll summary
          </span>
          <span className="font-sans text-[12px] font-bold text-[#0b0b0f]">$27.85</span>
        </div>

        <div className="flex flex-col gap-1.5 px-3 py-1.5">
          {/* Vehicle line */}
          <div className="flex items-center gap-2">
            <span className="relative shrink-0">
              <Image src="/images/tolls/camry-thumb.webp" alt="2025 Toyota Camry" width={96} height={96} className="h-9 w-9 rounded-lg object-cover"/>
              <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#0b0b0f] font-sans text-[8px] font-bold text-white">
                4
              </span>
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-sans text-[10px] font-semibold leading-tight text-[#0b0b0f]">2025 Toyota Camry</p>
              <p className="font-sans text-[8px] text-[#0b0b0f]/50">Plate: 8TJK274</p>
            </div>
            <span className="font-sans text-[10px] font-semibold text-[#0b0b0f]">$27.85</span>
          </div>

          {/* Toll events */}
          <div className="flex flex-col rounded-lg bg-black/[0.03] px-2 py-0.5">
            {TOLL_EVENTS.map((e, i) => (<div key={e.name} className={`flex items-center gap-2 py-1 ${i > 0 ? "border-t border-black/[0.06]" : ""}`}>
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-white shadow-[0_1px_2px_rgba(0,0,0,0.12)]">
                  <MapPin aria-hidden="true" className="h-2.5 w-2.5 text-[#0b0b0f]/70"/>
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-sans text-[9px] font-medium leading-tight text-[#0b0b0f]">{e.name}</p>
                  <p className="font-sans text-[7.5px] text-[#0b0b0f]/45">{e.when}</p>
                </div>
                <span className="font-sans text-[9px] font-semibold text-[#0b0b0f]">{e.amount}</span>
              </div>))}
          </div>

          {/* Totals */}
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center justify-between font-sans text-[9px] text-[#0b0b0f]/70">
              <span>Toll charges</span>
              <span>$27.85</span>
            </div>
            <div className="mt-0.5 flex items-center justify-between border-t border-black/[0.08] pt-1.5 font-sans text-[12px] font-bold text-[#0b0b0f]">
              <span>Total</span>
              <span>$27.85</span>
            </div>
          </div>
        </div>
      </div>

      {/* Paid banner — layered over the invoice, ~2/3 down and shifted right so it sits half-on/half-off; fades in/out on an 8s loop with a green shimmer sweep */}
      <div className="tolls-paid-banner absolute -right-3 top-2/3 z-10 flex w-max -translate-y-1/2 items-center gap-2.5 overflow-hidden rounded-xl border border-[#1f9d55]/40 bg-[#e7f6ec] px-3 py-2.5 shadow-[0_10px_24px_rgba(0,0,0,0.25)]">
        <span aria-hidden="true" className="anim-paid-sheen pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 bg-[linear-gradient(100deg,transparent_25%,rgba(255,255,255,0.7)_50%,transparent_75%)]"/>
        <span className="relative flex items-center gap-2">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#1f9d55]">
            <Check aria-hidden="true" className="h-3 w-3 text-white"/>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-sans text-[11px] font-bold leading-none text-[#0b3d22]">Paid</span>
            <span className="mt-0.5 font-sans text-[10px] font-semibold leading-none text-[#1f9d55]">$27.85</span>
          </span>
        </span>
        <span className="relative flex shrink-0 items-center gap-1 whitespace-nowrap border-l border-[#1f9d55]/30 pl-2.5 font-sans text-[8px] font-medium text-[#0b3d22]/80">
          <CreditCard aria-hidden="true" className="h-3 w-3"/>
          Card on file
        </span>
      </div>
    </div>);
}
/* ── AGREEMENTS — a static product mockup showing a generated vehicle rental agreement, the
   booking summary card and the in-app Documents screen. */
export function AgreementsBuilder() {
    return (<div className="relative mx-auto aspect-[3/4] w-full max-w-[236px]" role="img" aria-label="A generated vehicle rental agreement alongside the in-app Documents screen with access to the signed contract">
      {/* Bluish-purple glow sitting behind the agreement mockups */}
      <span aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[80%] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(124,77,255,0.65)_0%,rgba(79,70,229,0.32)_45%,transparent_72%)] blur-2xl"/>
      <Image src="/images/agreements/rental-agreement-mockup.webp" alt="Vehicle rental agreement document, booking summary and the in-app Documents screen" fill sizes="236px" className="object-contain [mask-image:linear-gradient(to_bottom,#000_72%,transparent_100%)]"/>
    </div>);
}
/* ── FLEET — the single-vehicle preview lives in ./fleet-vehicle-preview (client component). */
/* ── CRM — the messenger preview lives in ./crm-chat-preview (client component). */
